import { formatFinanceDate } from "@/utils/format-finance-date";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  DatePicker,
  DrawerClose,
  Button,
  DrawerFooter,
  Label,
  Input,
  Skeleton,
  MoneyInput,
  MultiSelect,
  Option,
} from "@/components/ui";
import { UpdateFinanceEntryValidationSchema } from "@/shared";
import {
  DisplayError,
  formatDateForDatePicker,
  formatDateToBackend,
  isDateValid,
} from "@/utils";

import {
  useCreateFinanceCategoryMutation,
  useFetchFinanceAccountsQuery,
  useFetchFinanceCategoriesQuery,
  useFetchFinanceEntryByIdQuery,
  usePermission,
  useToast,
  useUpdateFinanceEntryMutation,
} from "@/hooks";
import { UpdateFinanceEntryRequestDTO } from "@/types";
import { defaultEntriesCategories, defaultFinancialAccounts } from "@/helpers";

import { MessageError } from "../../message-error";
import { useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";

type Props = {
  handleClose: () => void;
  id: string;
  patientId?: string;
};

export function FormUpdateFinanceEntry({ handleClose, id, patientId }: Props) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");

  const { id: accountId } = useParams<{ id: string }>();

  const { toast } = useToast();

  const [selectedAccount, setSelectedAccount] = useState<Option | undefined>();

  const { data: entry, isLoading: isLoadingEntry } =
    useFetchFinanceEntryByIdQuery({ id });
  const { mutateAsync, isLoading } = useUpdateFinanceEntryMutation();
  const { data: dataAccounts, isLoading: isLoadingAccounts } =
    useFetchFinanceAccountsQuery({ page });
  const { data: dataCategories, isLoading: isLoadingCategories } =
    useFetchFinanceCategoriesQuery({});

  // O-17 (SPEC-NAV-01 §5): the category is a dependency of this entry. The
  // create endpoint (`StoreFinanceCategorieEntryRequest`) requires `name` and
  // nothing else, so the entry's `type` is not part of the payload.
  const canCreateCategory = checkPermission("category", "create categories");
  const { mutateAsync: createCategory } = useCreateFinanceCategoryMutation();
  const [createdCategories, setCreatedCategories] = useState<Option[]>([]);
  const [categoryCreateError, setCategoryCreateError] = useState<string | null>(
    null
  );

  async function handleCreateCategory(name: string): Promise<Option> {
    setCategoryCreateError(null);
    try {
      const { data } = await createCategory({ name });
      const option: Option = { value: data.id, label: data.name };
      setCreatedCategories((current) => [...current, option]);
      return option;
    } catch (error) {
      const message = DisplayError(error, t("MESSAGE.error_message"));
      setCategoryCreateError(message);
      toast({
        title: t("LABEL.error"),
        description: message,
        variant: "destructive",
      });
      throw error;
    }
  }

  const initialValues = {
    id: entry?.id || "",
    type: entry?.type || "",
    description: entry?.name || "",
    date: formatFinanceDate(entry?.date || "") || "",
    value: entry?.value || "",
    fine_for_delay: entry?.fine_for_delay || "",
    interest_rate: entry?.interest_rate || "",
    payment_method: entry?.payment_method || "",
    pay: entry?.pay || "0",
    payday:
      !isDateValid(entry?.payday) && entry?.payday
        ? formatDateForDatePicker(entry.payday)
        : isDateValid(entry?.payday)
          ? entry?.payday
          : "",
    mode: entry?.mode || "",
    entry: entry?.entry || "",
    end_date: entry?.period?.end_date || "",
    accountId: entry?.account.id || accountId || "",
    categoryId: entry?.category.id || "",
    patientId: patientId || entry?.patient.id || "",
    procedureId: entry?.procedure.id || "",
    employeeId: entry?.employee.id || "",
    type_periodic: entry?.type_periodic || "",
    total_installments: entry?.total_installments || "",
  };

  const { handleSubmit, getFieldProps, errors, touched, ...formik } =
    useFormik<UpdateFinanceEntryRequestDTO>({
      initialValues,
      validationSchema: UpdateFinanceEntryValidationSchema(),
      onSubmit: async (financeEntryData) => {
        await updateFinanceEntry(financeEntryData);
      },
    });

  async function updateFinanceEntry(entryData: UpdateFinanceEntryRequestDTO) {
    try {
      if (entryData.entry) entryData.entry = String(entryData.entry);

      if (!isDateValid(entryData.payday)) entryData.payday = "";
      entryData.value = String(entryData.value);
      entryData.interest_rate = String(entryData.interest_rate);
      entryData.fine_for_delay = String(entryData.fine_for_delay)?.replace(
        "%",
        ""
      );

      const request = {
        ...entryData,
        date: formatDateToBackend(entryData.date),
        payday: formatDateToBackend(entryData.payday || ""),
      };
      await mutateAsync(request, {
        onSuccess: ({ message }) => {
          handleClose();
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message.toLowerCase()}`),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error: any) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  useEffect(() => {
    if (id && entry) {
      formik.setValues(initialValues);
    }
  }, [id, entry, accountId]);

  useQuery({
    queryKey: ["finance-account", formik.values.accountId],
    queryFn: async () => {
      if (!formik.values.patientId) return null;
      const { data } = await httpRequest.get(
        `/finance-accounts/${formik.values.accountId}`
      );
      return data;
    },
    enabled: !!formik.values.accountId && !selectedAccount,
    onSuccess: (data) => {
      if (data) {
        setSelectedAccount({
          value: data.id,
          label: data.name,
        });
      }
    },
  });


  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="max-2xl:h-[94vh] max-sm:h-[90vh] max-2xl:overflow-y-auto"
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div className="space-x-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {!entry && isLoadingEntry ? (
              Array.from({ length: 11 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))
            ) : (
              <>
                <div className="space-y-1">
                  <Label>{t("LABEL.account")}</Label>

                  <MultiSelect
                    options={
                      dataAccounts?.items.map((account) => ({
                        value: account.id,
                        label: defaultFinancialAccounts.includes(account.name)
                          ? t(account.name.toLowerCase())
                          : account.name,
                      })) || []
                    }
                    loading={isLoadingAccounts || !dataAccounts}
                    selectedOptions={selectedAccount ? [selectedAccount] : []}
                    placeholder="Selecione uma conta..."
                    isInvalid={touched.accountId && errors.accountId}
                    isValid={touched.accountId && !errors.accountId}
                    value={formik.values.accountId}
                    onChange={(value) => {
                      formik.setFieldValue("accountId", value);
                      const selected = dataAccounts?.items.find(
                        (p) => p.id === value
                      );
                      if (selected) {
                        setSelectedAccount({
                          value: selected.id,
                          label: selected.name,
                        });
                      }
                    }}
                    onBlur={() => formik.handleBlur("accountId")}
                    messageError={errors.accountId}
                    disabled={!patientId}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t("LABEL.financy_account_type")}</Label>

                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("type")}
                      onValueChange={(value) => {
                        formik.setFieldValue("type", value);
                      }}
                      defaultValue={formik.values.type}
                    >
                      <SelectTrigger
                        isInvalid={touched.type && errors.type}
                        isValid={touched.type && !errors.type}
                      >
                        <SelectValue placeholder="Selecione o tipo da conta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="income">
                            {t("LABEL.income")}
                          </SelectItem>
                          <SelectItem value="expense">
                            {t("LABEL.expense")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError messageError={errors.type} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>{t("LABEL.category")}</Label>
                  <p className="text-xs text-app-fg-muted">
                    Categorias organizam receitas e despesas. O tipo do lançamento define a movimentação.
                  </p>

                  <MultiSelect
                    options={
                      dataCategories?.map((category) => ({
                        value: category.id,
                        label: defaultEntriesCategories.includes(category.name.toLowerCase())
                          ? t(`LABEL.${category.name.toLowerCase()}`)
                          : category.name,
                      })) || []
                    }
                    selectedOptions={createdCategories}
                    loading={isLoadingCategories || !dataCategories}
                    placeholder="Selecione a categoria..."
                    isInvalid={
                      Boolean(categoryCreateError) ||
                      (touched.categoryId && errors.categoryId)
                    }
                    isValid={
                      !categoryCreateError && touched.categoryId && !errors.categoryId
                    }
                    value={formik.values.categoryId}
                    onChange={(value) => {
                      setCategoryCreateError(null);
                      formik.setFieldValue("categoryId", value);
                    }}
                    onBlur={() => formik.handleBlur("categoryId")}
                    messageError={categoryCreateError ?? errors.categoryId}
                    allowCreate={canCreateCategory}
                    onCreateOption={handleCreateCategory}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t("LABEL.description")}</Label>
                  <Input
                    {...getFieldProps("description")}
                    isInvalid={touched.description && errors.description}
                    isValid={touched.description && !errors.description}
                    messageError={errors.description}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t("LABEL.maturity")}</Label>
                  <DatePicker
                    {...getFieldProps("date")}
                    isInvalid={touched.date && !!errors.date}
                    isValid={touched.date && !errors.date}
                    messageError={errors.date}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t("LABEL.value")}</Label>
                  <MoneyInput
                    placeholder="Valor"
                    isInvalid={touched.value && errors.value}
                    isValid={touched.value && !errors.value}
                    {...getFieldProps("value")}
                    messageError={errors.value}
                    defaultValue={formik.values.value}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Multa por atraso (%)</Label>
                  <Input
                    type="number"
                    placeholder="0%"
                    isInvalid={touched.fine_for_delay && errors.fine_for_delay}
                    isValid={touched.fine_for_delay && !errors.fine_for_delay}
                    {...getFieldProps("fine_for_delay")}
                    messageError={errors.fine_for_delay}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Juros ao mês (%)</Label>
                  <Input
                    type="number"
                    placeholder="0%"
                    isInvalid={touched.interest_rate && errors.interest_rate}
                    isValid={touched.interest_rate && !errors.interest_rate}
                    {...getFieldProps("interest_rate")}
                    messageError={errors.interest_rate}
                  />
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <div className="w-full border-b border-teal-500 py-5 flex items-center gap-1">
              <ChevronRight size={20} className="text-teal-700" />
              <h2 className="text-xl font-bold text-teal-700 mt-1">
                Pagamento
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {!entry && isLoadingEntry ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full" />
                ))
              ) : (
                <>
                  <div className="space-y-1">
                    <Label>Pago?</Label>

                    <div className="space-y-1">
                      <Select
                        {...getFieldProps("pay")}
                        onValueChange={(value) => {
                          formik.setFieldValue("pay", value);
                        }}
                        disabled={isDateValid(entry?.status)}
                        defaultValue={
                          isDateValid(entry?.status) ? "1" : formik.values.pay
                        }
                        value={
                          isDateValid(entry?.status) ? "1" : formik.values.pay
                        }
                        key={formik.values.pay}
                      >
                        <SelectTrigger
                          isInvalid={touched.pay && errors.pay}
                          isValid={touched.pay && !errors.pay}
                          className="disabled:opacity-100"
                        >
                          <SelectValue placeholder="Selecione a forma de pagamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="1">Sim, está pago!</SelectItem>
                            <SelectItem value="0">Não está pago!</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <MessageError messageError={errors.pay} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Data de pagamento</Label>
                    <DatePicker
                      {...getFieldProps("payday")}
                      isInvalid={touched.payday && !!errors.payday}
                      isValid={touched.payday && !errors.payday}
                      messageError={errors.payday}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t("LABEL.payment method")}</Label>

                    <div className="space-y-1">
                      <Select
                        {...getFieldProps("payment_method")}
                        onValueChange={(value) => {
                          formik.setFieldValue("payment_method", value);
                        }}
                        defaultValue={formik.values.payment_method}
                        key={formik.values.payment_method}
                      >
                        <SelectTrigger
                          isInvalid={
                            touched.payment_method && errors.payment_method
                          }
                          isValid={
                            touched.payment_method && !errors.payment_method
                          }
                        >
                          <SelectValue placeholder="Selecione a forma de pagamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="Pix">Pix</SelectItem>
                            <SelectItem value="Money">Dinheiro</SelectItem>
                            <SelectItem value="CreditCard">Cartão de crédito</SelectItem>
                            <SelectItem value="DebitCard">Cartão de débito</SelectItem>
                            <SelectItem value="Ticket">Boleto</SelectItem>
                            <SelectItem value="AutomaticDebit">
                              Débito automático
                            </SelectItem>
                            <SelectItem value="BankDeposit">
                              Depósito bancário
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <MessageError messageError={errors.payment_method} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <DrawerFooter className="flex flex-row justify-center pb-24 gap-2">
          <Button
            type="submit"
            className="px-10"
            isLoading={isLoading}
            disabled={!checkPermission("finance entry", "update finance entry") || isDateValid(entry?.status) || isLoading}
          >
            {t("LABEL.save")}
          </Button>
          <DrawerClose>
            <Button type="button" variant="outline">
              {t("LABEL.cancel")}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </div>
    </form>
  );
}
